import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  setDoc,
  collection,
  query,
  orderBy,
  getDocs,
  addDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import {
  ChevronDownIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import { Listbox } from "@headlessui/react";
import toast from "react-hot-toast";

// MultiSelect Component
function MultiSelect({
  options,
  selectedValues,
  onChange,
  placeholder,
  label,
}) {
  const [selected, setSelected] = useState(selectedValues || []);

  // Update local state when selectedValues prop changes
  useEffect(() => {
    setSelected(selectedValues || []);
  }, [selectedValues]);

  const handleSelectionChange = (newSelection) => {
    setSelected(newSelection);
    onChange(newSelection);
  };

  const toggleOption = (optionValue) => {
    const newSelection = selected.includes(optionValue)
      ? selected.filter((item) => item !== optionValue)
      : [...selected, optionValue];
    handleSelectionChange(newSelection);
  };

  return (
    <div className="relative">
      <Listbox value={selected} onChange={handleSelectionChange}>
        <div className="relative">
          <Listbox.Button className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-left">
            <span className="block truncate">
              {selected.length === 0 ? placeholder : selected.join(", ")}
            </span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDownIcon className="h-4 w-4 text-gray-400" />
            </span>
          </Listbox.Button>
          <Listbox.Options className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            {options.map((option) => {
              const optionValue = option.description || option;
              const isSelected = selected.includes(optionValue);

              return (
                <div
                  key={option.id || option}
                  className="relative cursor-pointer select-none py-2 px-3 hover:bg-primary-100 hover:text-primary-900"
                  onClick={() => toggleOption(optionValue)}
                >
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="block truncate">{optionValue}</span>
                  </div>
                </div>
              );
            })}
          </Listbox.Options>
        </div>
      </Listbox>
    </div>
  );
}

// KeywordInput: creatable multi-select with typeahead (same pattern as LessonEntry)
function KeywordInput({
  selectedKeywords,
  onChange,
  existingKeywords,
  onAddNewKeyword,
  placeholder = "Type to search or add keywords...",
}) {
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const normalizedExisting = (existingKeywords || [])
    .map((k) => (typeof k === "string" ? k : k.value || k.id || ""))
    .filter(Boolean);
  const query = (inputValue || "").trim().toLowerCase();
  const selectedSet = new Set(
    (selectedKeywords || []).map((k) => k.toLowerCase()),
  );
  const matched = (
    query
      ? normalizedExisting.filter((v) => v.toLowerCase().includes(query))
      : normalizedExisting
  ).filter((v) => !selectedSet.has(v.toLowerCase()));
  const exactMatch =
    query && normalizedExisting.some((v) => v.toLowerCase() === query);
  const canAddNew = query && !exactMatch && !selectedSet.has(query);
  const options = canAddNew
    ? [
        { addNew: true, label: `Add "${(inputValue || "").trim()}"` },
        ...matched,
      ]
    : matched;
  const optionCount = options.length;

  const closeDropdown = () => {
    setShowDropdown(false);
    setHighlightIndex(0);
  };

  const addKeyword = (value) => {
    const trimmed = (value || "").trim();
    if (!trimmed || selectedSet.has(trimmed.toLowerCase())) return;
    onChange([...(selectedKeywords || []), trimmed]);
    setInputValue("");
    closeDropdown();
  };

  const removeKeyword = (toRemove) => {
    onChange((selectedKeywords || []).filter((k) => k !== toRemove));
  };

  const handleKeyDown = (e) => {
    if (!showDropdown && e.key !== "Escape") {
      setShowDropdown(true);
    }
    if (e.key === "Escape") {
      closeDropdown();
      return;
    }
    if (e.key === "Enter" && showDropdown && optionCount > 0) {
      e.preventDefault();
      const opt = options[highlightIndex];
      if (opt && typeof opt === "object" && opt.addNew) {
        addKeyword((inputValue || "").trim());
        if (onAddNewKeyword) onAddNewKeyword((inputValue || "").trim());
      } else {
        const value =
          typeof opt === "string"
            ? opt
            : (opt && opt.value) || (opt && opt.label);
        if (value) addKeyword(value);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => (i + 1) % optionCount);
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => (i - 1 + optionCount) % optionCount);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".keyword-input-container"))
        setShowDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="keyword-input-container relative">
      <div className="flex flex-wrap gap-2 p-2 border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent bg-white min-h-[42px]">
        {(selectedKeywords || []).map((kw) => (
          <span
            key={kw}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-sm bg-primary-100 text-primary-800"
          >
            {kw}
            <button
              type="button"
              onClick={() => removeKeyword(kw)}
              className="hover:bg-primary-200 rounded p-0.5"
              aria-label={`Remove ${kw}`}
            >
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setShowDropdown(true);
            setHighlightIndex(0);
          }}
          onFocus={() => setShowDropdown(true)}
          onKeyDown={handleKeyDown}
          placeholder={(selectedKeywords || []).length === 0 ? placeholder : ""}
          className="flex-1 min-w-[120px] px-1 py-0.5 border-0 focus:ring-0 focus:outline-none text-sm"
        />
      </div>
      {showDropdown && (matched.length > 0 || canAddNew) && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {options.map((opt, idx) => {
            if (opt && typeof opt === "object" && opt.addNew) {
              return (
                <div
                  key="__addnew__"
                  className={`px-3 py-2 cursor-pointer text-sm ${idx === highlightIndex ? "bg-primary-100 text-primary-900" : "hover:bg-gray-100"}`}
                  onClick={() => {
                    addKeyword((inputValue || "").trim());
                    if (onAddNewKeyword)
                      onAddNewKeyword((inputValue || "").trim());
                  }}
                >
                  {opt.label}
                </div>
              );
            }
            const value =
              typeof opt === "string"
                ? opt
                : (opt && opt.value) || (opt && opt.label) || "";
            const isSelected = selectedSet.has(value.toLowerCase());
            if (isSelected) return null;
            return (
              <div
                key={value}
                className={`px-3 py-2 cursor-pointer text-sm ${idx === highlightIndex ? "bg-primary-100 text-primary-900" : "hover:bg-gray-100"}`}
                onClick={() => addKeyword(value)}
              >
                {value}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function VideoEntry() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [units, setUnits] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [videoUrl, setVideoUrl] = useState("");
  const [coverImage, setCoverImage] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState("");
  const [existingCoverImage, setExistingCoverImage] = useState("");

  // Collections data
  const [unitsData, setUnitsData] = useState([]);
  const [keywordsData, setKeywordsData] = useState([]);

  // Vimeo video selection state
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [cachedVideos, setCachedVideos] = useState([]);

  // Fetch units from Firestore
  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const unitsRef = collection(db, "units");
        const q = query(unitsRef, orderBy("description"));
        const querySnapshot = await getDocs(q);

        const unitsList = [];
        querySnapshot.forEach((doc) => {
          unitsList.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        setUnitsData(unitsList);
      } catch (error) {
        console.error("Error fetching units:", error);
      }
    };

    fetchUnits();
  }, []);

  // Fetch keywords from Firestore (for typeahead; same collection as lessons)
  useEffect(() => {
    const fetchKeywords = async () => {
      try {
        const keywordsRef = collection(db, "keywords");
        const snapshot = await getDocs(keywordsRef);
        const list = [];
        snapshot.forEach((d) => {
          const v = d.data().value;
          if (v) list.push({ id: d.id, value: v });
        });
        list.sort((a, b) => (a.value || "").localeCompare(b.value || ""));
        setKeywordsData(list);
      } catch (err) {
        console.error("Error fetching keywords:", err);
      }
    };
    fetchKeywords();
  }, []);

  // Fetch video data
  useEffect(() => {
    const fetchVideo = async () => {
      try {
        if (videoId === "new") {
          setVideo({ id: "new" });
          setLoading(false);
          return;
        }

        const videoRef = doc(db, "videos", videoId);
        const videoSnap = await getDoc(videoRef);

        if (videoSnap.exists()) {
          const videoData = {
            id: videoSnap.id,
            ...videoSnap.data(),
          };
          setVideo(videoData);
          setTitle(videoData.title || "");
          setDescription(videoData.description || "");
          setUnits(videoData.units || []);
          setKeywords(videoData.keywords || []);
          setVideoUrl(videoData.video_url || "");
          // Set existing cover image if available
          if (videoData.cover_image) {
            setCoverImagePreview(videoData.cover_image);
            setExistingCoverImage(videoData.cover_image);
          }
        } else {
          setError("Video not found");
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching video:", error);
        setError("Failed to load video");
        setLoading(false);
      }
    };

    fetchVideo();
  }, [videoId]);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCoverImage(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setCoverImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClearImage = () => {
    setCoverImage(null);
    setCoverImagePreview(existingCoverImage || "");
  };

  const handleSave = async () => {
    try {
      let coverImageUrl = existingCoverImage; // Preserve existing image URL

      // Upload cover image if a new one is selected
      if (coverImage) {
        const timestamp = Date.now();
        const fileName = `images/videos/${timestamp}_${coverImage.name}`;
        const storageRef = ref(storage, fileName);

        const uploadResult = await uploadBytes(storageRef, coverImage);
        coverImageUrl = await getDownloadURL(uploadResult.ref);
      }

      const videoData = {
        title,
        description,
        units,
        keywords,
        video_url: videoUrl,
        cover_image: coverImageUrl,
        updated_at: new Date(),
      };

      // Determine if this is a new video or existing video
      let isNewVideo = video.id === "new";

      // If we have a video ID that's not "new", verify the video exists
      if (!isNewVideo && video.id) {
        try {
          const videoRef = doc(db, "videos", video.id);
          const videoSnap = await getDoc(videoRef);
          isNewVideo = !videoSnap.exists();
        } catch (error) {
          console.error("Error checking video existence:", error);
          // If we can't verify, assume it's new to be safe
          isNewVideo = true;
        }
      }

      if (isNewVideo) {
        // Create new video document
        const videosRef = collection(db, "videos");
        const docRef = await addDoc(videosRef, {
          ...videoData,
          created_date: new Date(),
        });

        // Update the video state with the new ID
        setVideo({ id: docRef.id, ...videoData, created_date: new Date() });

        toast.success("Video saved successfully!");
      } else {
        // Update existing video document
        const videoRef = doc(db, "videos", video.id);
        await updateDoc(videoRef, videoData);

        toast.success("Video updated successfully!");
      }
    } catch (error) {
      console.error("Error saving video:", error);
      toast.error("Error saving video. Please try again.");
    }
  };

  const handleSaveAndClose = async () => {
    await handleSave();
    navigate("/admin/videos");
  };

  const handleSaveAndNew = async () => {
    await handleSave();
    // Reset form for new video
    setTitle("");
    setDescription("");
    setUnits([]);
    setKeywords([]);
    setVideoUrl("");
    setCoverImage(null);
    setCoverImagePreview("");
    setExistingCoverImage("");
    setVideo({ id: "new" });
  };

  const handleCancel = () => {
    const hasChanges =
      title ||
      description ||
      units.length > 0 ||
      keywords.length > 0 ||
      videoUrl ||
      coverImage;

    if (hasChanges) {
      const confirmed = window.confirm(
        "You have unsaved changes. Are you sure you want to cancel?",
      );
      if (!confirmed) {
        return;
      }
    }

    navigate("/admin/videos");
  };

  const openVideoDialog = async () => {
    if (!title.trim()) {
      toast.error("Please enter a video title first to search for videos.");
      return;
    }

    setShowVideoDialog(true);

    // If we have cached videos, use them
    if (cachedVideos.length > 0) {
      const videoTitle = title.toLowerCase();
      const filtered = cachedVideos.filter((video) =>
        video.name.toLowerCase().includes(videoTitle),
      );
      setFilteredVideos(filtered);
      return;
    }

    // Otherwise, fetch videos from API
    setLoadingVideos(true);

    try {
      const videoTitle = title.toLowerCase();
      let allVideos = [];
      let page = 1;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await fetch(
          `https://api.vimeo.com/users/129001864/projects/26591581/videos?page=${page}&per_page=100`,
          {
            headers: {
              Authorization: "Bearer 71784e0083b4a9fccd6b1cb1ec310a0f",
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const videosList = data.data.map((video) => ({
          id: video.uri.split("/").pop(),
          name: video.name,
          description: video.description,
          player_url: video.player_embed_url,
          duration: video.duration,
          created_time: video.created_time,
        }));

        allVideos = [...allVideos, ...videosList];

        // Check if there are more pages
        if (data.paging && data.paging.next) {
          page++;
        } else {
          hasMorePages = false;
        }
      }

      // Cache all videos for this session
      setCachedVideos(allVideos);

      // Filter videos by title only
      const filtered = allVideos.filter((video) =>
        video.name.toLowerCase().includes(videoTitle),
      );

      setFilteredVideos(filtered);
    } catch (error) {
      console.error("Error fetching videos:", error);
      toast.error("Error loading videos. Please try again.");
    } finally {
      setLoadingVideos(false);
    }
  };

  const selectVideo = (video) => {
    const videoUrl =
      video.player_url || `https://player.vimeo.com/video/${video.id}`;
    setVideoUrl(videoUrl);
    setShowVideoDialog(false);
    toast.success("Video selected successfully!");
  };

  const closeVideoDialog = () => {
    setShowVideoDialog(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <p className="mt-2 text-gray-600">Loading video...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="py-8 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-primary-600">
              {video?.id === "new" ? "New Video" : "Edit Video"}
            </h1>
          </div>

          <div className="flex">
            {/* Left Column */}
            <div className="flex-1 pr-8">
              <div className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter video title"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter video description"
                  />
                </div>

                {/* Units */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Units
                  </label>
                  <MultiSelect
                    options={unitsData}
                    selectedValues={units}
                    onChange={setUnits}
                    placeholder="Please select units..."
                  />
                </div>

                {/* Keywords */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Keywords
                  </label>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <KeywordInput
                        selectedKeywords={keywords}
                        onChange={setKeywords}
                        existingKeywords={keywordsData}
                        onAddNewKeyword={async (value) => {
                          const trimmed = (value || "").trim();
                          if (!trimmed) return;
                          const id = trimmed.toLowerCase().replace(/\s+/g, "-");
                          try {
                            await setDoc(
                              doc(db, "keywords", id),
                              { value: trimmed },
                              { merge: true },
                            );
                            setKeywordsData((prev) => {
                              if (
                                prev.some(
                                  (k) =>
                                    (k.value || "").toLowerCase() ===
                                    trimmed.toLowerCase(),
                                )
                              )
                                return prev;
                              return [...prev, { id, value: trimmed }].sort(
                                (a, b) =>
                                  (a.value || "").localeCompare(b.value || ""),
                              );
                            });
                          } catch (err) {
                            console.error("Error saving keyword:", err);
                          }
                        }}
                        placeholder="Type to search or add keywords..."
                      />
                    </div>
                    <div className="flex shrink-0 gap-1 pt-1">
                      <button
                        type="button"
                        onClick={() => {
                          try {
                            localStorage.setItem(
                              "kuzko-copied-keywords",
                              JSON.stringify(keywords || []),
                            );
                            toast.success("Keywords copied");
                          } catch (e) {
                            toast.error("Could not copy keywords");
                          }
                        }}
                        title="Copy keywords"
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                        aria-label="Copy keywords"
                      >
                        <ClipboardDocumentIcon className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          try {
                            const raw = localStorage.getItem("kuzko-copied-keywords");
                            const pasted = raw ? JSON.parse(raw) : [];
                            if (!Array.isArray(pasted) || pasted.length === 0) {
                              toast.error("No keywords to paste");
                              return;
                            }
                            const currentLower = new Set(
                              (keywords || []).map((k) =>
                                (k || "").toLowerCase(),
                              ),
                            );
                            const toAdd = pasted.filter(
                              (k) =>
                                k && !currentLower.has((k || "").toLowerCase()),
                            );
                            if (toAdd.length === 0) {
                              toast.success("All keywords already present");
                              return;
                            }
                            setKeywords([...(keywords || []), ...toAdd]);
                            toast.success(`Added ${toAdd.length} keyword(s)`);
                          } catch (e) {
                            toast.error("Could not paste keywords");
                          }
                        }}
                        title="Paste keywords"
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                        aria-label="Paste keywords"
                      >
                        <ClipboardDocumentListIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Video URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Video URL
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={videoUrl}
                      onChange={(e) => setVideoUrl(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter video URL or click 'Select Video' to choose from Vimeo"
                    />
                    <button
                      type="button"
                      onClick={openVideoDialog}
                      className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                    >
                      Select Video
                    </button>
                  </div>
                  {videoUrl && (
                    <p className="mt-1 text-sm text-gray-500">
                      Video URL: {videoUrl}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="w-1/2 pl-8">
              <div className="space-y-6">
                {/* Upload Cover */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cover Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="cover-upload"
                  />
                  <div className="flex space-x-2">
                    <label
                      htmlFor="cover-upload"
                      className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors cursor-pointer inline-block"
                    >
                      Upload Cover
                    </label>
                    {(coverImage || existingCoverImage) && (
                      <button
                        type="button"
                        onClick={handleClearImage}
                        className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="mt-2 w-[360px] h-[233px] bg-gray-200 rounded-md flex items-center justify-center overflow-hidden">
                    {coverImagePreview ? (
                      <img
                        src={coverImagePreview}
                        alt="Cover preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-gray-500 text-xs">
                        No image selected
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4 mt-8">
            {video.id === "new" ? (
              <>
                <button
                  onClick={handleSaveAndClose}
                  className="bg-primary-600 text-white px-6 py-2 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Save & Close
                </button>
                <button
                  onClick={handleSaveAndNew}
                  className="bg-primary-600 text-white px-6 py-2 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Save & New
                </button>
                <button
                  onClick={handleCancel}
                  className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleSaveAndClose}
                  className="bg-primary-600 text-white px-6 py-2 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Save
                </button>
                <button
                  onClick={handleCancel}
                  className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Video Selection Dialog */}
      {showVideoDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Select Video
                </h3>
                <button
                  onClick={closeVideoDialog}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Showing videos that match:{" "}
                  <span className="font-medium">"{title}"</span>
                </p>
                {loadingVideos ? (
                  <div className="flex items-center space-x-2">
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
                    <p className="text-sm text-gray-500">Loading videos...</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    Found {filteredVideos.length} matching videos
                  </p>
                )}
              </div>

              <div className="max-h-96 overflow-y-auto space-y-2">
                {loadingVideos ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mb-2"></div>
                    <p className="text-gray-500">Searching for videos...</p>
                  </div>
                ) : filteredVideos.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    No videos found matching "{title}". Try a different video
                    title.
                  </p>
                ) : (
                  filteredVideos.map((video) => (
                    <div
                      key={video.id}
                      className="border border-gray-200 rounded-md p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => selectVideo(video)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 mb-1">
                            {video.name}
                          </h4>
                          {video.description && (
                            <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                              {video.description}
                            </p>
                          )}
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span>
                              Duration: {Math.floor(video.duration / 60)}:
                              {(video.duration % 60)
                                .toString()
                                .padStart(2, "0")}
                            </span>
                            <span>ID: {video.id}</span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            selectVideo(video);
                          }}
                          className="bg-primary-600 text-white px-3 py-1 rounded text-sm hover:bg-primary-700 transition-colors"
                        >
                          Select
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="flex justify-end mt-4">
                <button
                  onClick={closeVideoDialog}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VideoEntry;
